import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CaseIntakeApi, {
  CaseIntakeParsed,
  CaseWithCitation,
  IntakeClientCandidate,
  IntakeMatchReason,
  IntakeMatchResponse,
  IntakeReadStarted,
} from '../../api/CaseIntakeApi';
import { paymentLinkSendApi } from '../../api/PaymentApi';
import { createPaymentLinkSender } from '../../utils/paymentLinkSend';
import { sendPaymentAfterCreate, type AfterCreate } from '../../utils/paymentOnCreate';
import SendPaymentLinkBox, { usePaymentLinkBox } from '../SendPaymentLinkBox/SendPaymentLinkBox';
import { REFERRAL_SOURCE_LABELS, userSourceOptions } from '../../constants/referralSources';
import CourtApi, { FullCourtDto } from '../../api/CourtApi';
import ProductApi from '../../api/ProductApi';
import { FullProsecutorDto } from '../../api/ProsecutorApi';
import { ProductDto } from '../../interfaces/ProductInterfaces';
import {
  CaseIntakeField,
  CaseIntakeForm,
  fieldOrigin,
  formFromParsed,
  missingForCreate,
  nameProblem,
} from '../../utils/caseIntakeForm';
import { productMatchesTicketLicenseType } from '../../utils/productLicenseType';
import { isGenderPronounValid, PronounSet, TicketGender } from '../../utils/ticketGenderPronouns';
import CourtSelect from '../CourtSelect/CourtSelect';
import GenderPronounFields from '../GenderPronounFields/GenderPronounFields';
import ProsecutorSelect from '../ProsecutorSelect/ProsecutorSelect';
import type { CourtMatch } from '../UploadTicket/UploadTicket';

/** The fields that say who the client is and which ticket this is; a change asks the server again. */
function identityKeyOf(form: CaseIntakeForm): string {
  return [
    form.firstName,
    form.lastName,
    form.dateOfBirth,
    form.driversLicenseNumber,
    form.email,
    form.phone,
    form.ticketNumber,
  ].join('|');
}

const REASON_LABELS: Record<IntakeMatchReason, string> = {
  licence: 'Licence number',
  email: 'Email',
  phone: 'Phone',
  'name-dob': 'Name + date of birth',
  'name-only': 'Same name only',
};

const ORIGIN_TEXT = {
  document: 'From the document',
  empty: 'Not on the document',
  edited: 'Edited',
} as const;

function candidateName(candidate: IntakeClientCandidate): string {
  return [candidate.firstName, candidate.middleName, candidate.lastName].filter(Boolean).join(' ') || '(no name)';
}

/** Where the document and the client on file disagree. Shown, never written: the record is kept. */
function differences(form: CaseIntakeForm, candidate: IntakeClientCandidate): string[] {
  const out: string[] = [];
  const digits = (value: string | null) => (value ?? '').replace(/\D/g, '').slice(-10);
  if (form.phone && digits(form.phone) !== digits(candidate.phone)) {
    out.push(`Phone: the document says ${form.phone}; on file ${candidate.phone || 'none'}`);
  }
  if (form.email && form.email.toLowerCase() !== (candidate.email ?? '').toLowerCase()) {
    out.push(`Email: the document says ${form.email}; on file ${candidate.email || 'none'}`);
  }
  if (form.dateOfBirth && candidate.dateOfBirth && form.dateOfBirth !== candidate.dateOfBirth.slice(0, 10)) {
    out.push(`Date of birth: the document says ${form.dateOfBirth}; on file ${candidate.dateOfBirth.slice(0, 10)}`);
  }
  return out;
}

const DocumentPreview: React.FC<{ file: File | null; pastedText: string | null; fileName: string }> = ({
  file,
  pastedText,
  fileName,
}) => {
  const url = React.useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);
  const [imgFailed, setImgFailed] = React.useState(false);
  React.useEffect(() => {
    setImgFailed(false);
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [url]);

  if (pastedText !== null) {
    return (
      <Box component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', m: 0, fontSize: 14 }}>
        {pastedText}
      </Box>
    );
  }
  const isImageFile = Boolean(file && (file.type.startsWith('image/') || /\.hei[cf]$/i.test(file.name)));
  if (file && url && isImageFile && !imgFailed) {
    return (
      <Box
        component="img"
        src={url}
        alt={fileName}
        onError={() => setImgFailed(true)}
        sx={{ maxWidth: '100%' }}
      />
    );
  }
  if (file && url && (file.type === 'application/pdf' || /\.pdf$/i.test(file.name))) {
    return <Box component="iframe" src={url} title={fileName} sx={{ width: '100%', height: '75vh', border: 0 }} />;
  }
  // A saved web page is not rendered here: it could carry scripts, and this is the staff portal.
  return (
    <Typography color="text.secondary">
      No preview for {fileName}. It will be filed on the case as it is.
    </Typography>
  );
};

/**
 * The review screen of "Create case". Every field the document filled is shown and editable, with
 * where it came from; the client match is shown with its reasons; and Create stays off until the
 * essentials are there. This screen is the safeguard: the parse and the match can both be wrong.
 */
const CreateCaseReview: React.FC<{
  started: IntakeReadStarted;
  file: File | null;
  pastedText: string | null;
  /** The case is for this client: no matching and no "new client". */
  lockedClient: { userGuid: string; name: string } | null;
  /** After Create: what the one press sent, or why the payment step is needed (utils/paymentOnCreate). */
  onCreated: (ticketGuid: string, after: AfterCreate) => void;
}> = ({ started, file, pastedText, lockedClient, onCreated }) => {
  const existingDocumentGuid = started.existingDocumentGuid ?? null;

  // The read runs on the server while staff start on the form (Lloyd, Oct 2026: under two minutes
  // from the photo to the payment link). Its result fills only the fields nobody has typed in yet.
  const [reading, setReading] = React.useState(true);
  const [readError, setReadError] = React.useState<string | null>(null);
  const [parsed, setParsed] = React.useState<CaseIntakeParsed | null>(null);
  const [courtMatch, setCourtMatch] = React.useState<CourtMatch | null>(null);
  const [original, setOriginal] = React.useState(() => formFromParsed(null));
  const [form, setForm] = React.useState<CaseIntakeForm>(original.form);

  const [candidates, setCandidates] = React.useState<IntakeClientCandidate[]>([]);
  const [duplicates, setDuplicates] = React.useState<CaseWithCitation[]>([]);
  const [emailHeldBy, setEmailHeldBy] = React.useState<IntakeMatchResponse['emailHeldBy']>(null);
  const [clientChoice, setClientChoice] = React.useState<string | 'new' | null>(
    lockedClient ? lockedClient.userGuid : null,
  );
  const [choiceTouched, setChoiceTouched] = React.useState(Boolean(lockedClient));
  const [matching, setMatching] = React.useState(false);

  const [court, setCourt] = React.useState<FullCourtDto | null>(null);
  const [prosecutor, setProsecutor] = React.useState<FullProsecutorDto | null>(null);
  const [products, setProducts] = React.useState<ProductDto[]>([]);
  const [plan, setPlan] = React.useState('');
  const [gender, setGender] = React.useState<TicketGender>('');
  const [pronounSet, setPronounSet] = React.useState<PronounSet>('');
  const [hasTakenDsc, setHasTakenDsc] = React.useState(false);
  const [clientSource, setClientSource] = React.useState('');
  const [markOtr, setMarkOtr] = React.useState(false);
  const [clientVisible, setClientVisible] = React.useState<boolean | null>(null);
  // A document picked from the client's file to start a case is the citation, as staff chose it.
  const [isCitation, setIsCitation] = React.useState<boolean | null>(existingDocumentGuid ? true : null);
  const [duplicateAcknowledged, setDuplicateAcknowledged] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [submitError, setSubmitError] = React.useState<string | null>(null);

  const set = (field: CaseIntakeField) => (event: { target: { value: string } }) =>
    setForm((current) => ({ ...current, [field]: event.target.value }));
  const origin = (field: CaseIntakeField) =>
    reading ? 'Reading the document…' : ORIGIN_TEXT[fieldOrigin(field, form, original.form, original.fromDocument)];

  React.useEffect(() => {
    ProductApi.getProducts({ from: 0, to: 100 })
      .then((result) => setProducts(result.data))
      .catch((error) => console.error('Error fetching products:', error));
  }, []);

  const applyCourtGuid = (guid: string | null | undefined, onlyIfEmpty: boolean) => {
    if (!guid) return;
    CourtApi.getCourtById(guid)
      .then((result) => {
        setCourt((current) => (onlyIfEmpty && current ? current : result.court));
        if (!onlyIfEmpty) setProsecutor(null);
      })
      .catch((error) => console.error('Could not load the court:', error));
  };

  // Ask for the read until it is done. Fields staff have already typed are left as they are.
  const identityAtRead = React.useRef<string | null>(null);
  React.useEffect(() => {
    let cancelled = false;
    let timer: number | undefined;
    const poll = async () => {
      try {
        const state = await CaseIntakeApi.readResult(started.token);
        if (cancelled) return;
        if (state.status === 'reading') {
          timer = window.setTimeout(() => void poll(), 1500);
          return;
        }
        setReading(false);
        if (state.status === 'failed') {
          setReadError(state.error);
          return;
        }
        if (state.parseError) setReadError(state.parseError);
        const read = formFromParsed(state.parsed);
        setOriginal(read);
        setForm((current) => {
          const merged = { ...current };
          for (const field of Object.keys(read.form) as CaseIntakeField[]) {
            if (merged[field] === '' && read.form[field] !== '') {
              (merged as Record<CaseIntakeField, string>)[field] = read.form[field];
            }
          }
          identityAtRead.current = identityKeyOf(merged);
          return merged;
        });
        setParsed(state.parsed);
        setCourtMatch(state.courtMatch);
        if (state.courtMatch?.status === 'matched') applyCourtGuid(state.courtMatch.courtGuid, true);
        setDuplicates(state.duplicates);
        if (!lockedClient) {
          setCandidates(state.candidates);
          setEmailHeldBy(state.emailHeldBy);
          setClientChoice((current) => (choiceTouched ? current : state.preselected));
        }
        const citation = state.parsed?.documentKind === 'citation';
        setIsCitation((current) => current ?? citation);
        setClientVisible((current) => current ?? citation);
      } catch (error) {
        if (cancelled) return;
        setReading(false);
        setReadError(error instanceof Error ? error.message : 'The document could not be read.');
      }
    };
    void poll();
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [started.token]);

  // Staff correcting a name, phone, email, licence or citation number asks again who this is.
  const identityKey = identityKeyOf(form);
  React.useEffect(() => {
    if (reading || identityKey === identityAtRead.current) return undefined;
    const timer = window.setTimeout(() => {
      setMatching(true);
      CaseIntakeApi.match({
        firstName: form.firstName,
        lastName: form.lastName,
        dateOfBirth: form.dateOfBirth,
        driversLicenseNumber: form.driversLicenseNumber,
        email: form.email,
        phone: form.phone,
        ticketNumber: form.ticketNumber,
      })
        .then((result) => {
          identityAtRead.current = identityKey;
          setDuplicates((current) => {
            const same =
              current.map((d) => d.Ticket_guid).join() === result.duplicates.map((d) => d.Ticket_guid).join();
            if (!same) setDuplicateAcknowledged(false);
            return result.duplicates;
          });
          if (lockedClient) return;
          setCandidates(result.candidates);
          setEmailHeldBy(result.emailHeldBy);
          setClientChoice((current) => {
            if (!choiceTouched) return result.preselected;
            if (current && current !== 'new' && !result.candidates.some((c) => c.userGuid === current)) {
              return null;
            }
            return current;
          });
        })
        .catch((error) => console.error('Client match failed:', error))
        .finally(() => setMatching(false));
    }, 600);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identityKey, reading]);

  const matchingProducts = React.useMemo(
    () => products.filter((product) => productMatchesTicketLicenseType(product.Product_LicenseType, form.licenseType)),
    [products, form.licenseType],
  );
  React.useEffect(() => {
    if (plan && !matchingProducts.some((product) => product.Product_Name === plan)) setPlan('');
  }, [matchingProducts, plan]);

  const chosenCandidate = candidates.find((candidate) => candidate.userGuid === clientChoice) ?? null;
  const isNewClient = clientChoice === 'new';
  const strongCandidates = candidates.filter((candidate) =>
    candidate.reasons.some((reason) => reason !== 'name-only'),
  );

  const missing = missingForCreate({
    form,
    clientChoice,
    courtGuid: court?.Court_guid ?? null,
    plan,
    hasDuplicates: duplicates.length > 0,
    duplicateAcknowledged,
    genderPronounValid: isGenderPronounValid(gender, pronounSet),
  });

  // "Send payment link by email and text": with it ticked, Create also sends the link.
  const paymentBox = usePaymentLinkBox(
    !clientChoice
      ? null
      : isNewClient
        ? {
            source: clientSource || undefined,
            email: form.email.trim() || undefined,
            phone: form.phone.trim() || undefined,
            markOtr,
            plan: plan || undefined,
          }
        : { User_guid: clientChoice, markOtr, plan: plan || undefined },
  );

  const create = async () => {
    if (submitting || missing.length > 0 || paymentBox.problem || !court) return;
    setSubmitting(true);
    setSubmitError(null);
    const optional = (value: string) => value.trim() || undefined;
    try {
      const result = await CaseIntakeApi.create({
        ...(existingDocumentGuid
          ? { existingDocumentGuid }
          : { token: started.token, fileName: started.fileName }),
        documentClientVisible: clientVisible ?? false,
        documentIsCitation: isCitation ?? false,
        ...(isNewClient
          ? {
              User_FirstName: form.firstName.trim(),
              User_MiddleName: optional(form.middleName),
              User_LastName: form.lastName.trim(),
              User_DOB: optional(form.dateOfBirth),
              User_Email: optional(form.email),
              User_PhoneNumber: optional(form.phone),
              User_DriversLicenseNumber: optional(form.driversLicenseNumber),
              User_LicenseType: optional(form.licenseClass),
              User_Source: optional(clientSource),
            }
          : { User_guid: clientChoice ?? undefined }),
        Ticket_Number: form.ticketNumber.trim(),
        Ticket_Citation: form.citation.trim(),
        Ticket_Address: form.address.trim(),
        Ticket_City: form.city.trim(),
        Ticket_County: form.county.trim(),
        Ticket_CourtDate: form.courtDate,
        Ticket_RespondByDate: form.respondBy,
        Ticket_LicenseType: form.licenseType,
        Ticket_Zone: form.zone,
        Ticket_Representation: plan,
        Ticket_ArrestingAgency: optional(form.agency),
        Ticket_AgencyCaseNumber: optional(form.agencyCaseNumber),
        Ticket_LicensePlate: optional(form.plate),
        Ticket_Gender: gender || undefined,
        Ticket_PronounSet: pronounSet || undefined,
        Ticket_HasTakenDscInPastYear: hasTakenDsc,
        Court_guid: court.Court_guid,
        Prosecutor_guid: prosecutor?.Prosecutor_guid,
        markOtr,
        createDespiteDuplicate: duplicateAcknowledged,
        clientAddress: optional(form.clientAddress),
      });
      if (result.emailNotSaved) {
        window.alert(
          `${result.emailNotSaved} is already on file for another client, so the new client was saved ` +
            'without an email. An alert has been raised to get their own address.',
        );
      }
      // The same press sends the payment link when the box is ticked; nothing is sent otherwise.
      const after = await sendPaymentAfterCreate(
        result.ticketGuid,
        paymentBox.choice({ emailNotSaved: Boolean(result.emailNotSaved) }),
        createPaymentLinkSender(paymentLinkSendApi),
      );
      onCreated(result.ticketGuid, after);
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'The case could not be created.');
      setSubmitting(false);
    }
  };

  const textField = (
    field: CaseIntakeField,
    label: string,
    options: { type?: string; required?: boolean; error?: string | null; multiline?: boolean } = {},
  ) => (
    <TextField
      label={options.required ? `${label} (required)` : label}
      type={options.type}
      value={form[field]}
      onChange={set(field)}
      error={Boolean(options.error)}
      helperText={options.error || origin(field)}
      multiline={options.multiline}
      InputLabelProps={options.type === 'date' ? { shrink: true } : undefined}
      fullWidth
      size="small"
    />
  );

  const printedCourt = parsed?.courtName ?? null;

  return (
    <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 5fr) minmax(0, 6fr)' } }}>
      <Paper variant="outlined" sx={{ p: 2, alignSelf: 'start', position: { lg: 'sticky' }, top: { lg: 0 } }}>
        <Typography variant="subtitle2" gutterBottom>
          The document
        </Typography>
        <DocumentPreview file={file} pastedText={pastedText} fileName={started.fileName} />
      </Paper>

      <Stack spacing={3}>
        {reading ? (
          <Alert severity="info" icon={<CircularProgress size={20} />}>
            Reading the document. Fields fill in as soon as it is done — you can choose the plan and
            court meanwhile. Nothing is saved until you press Create.
          </Alert>
        ) : readError ? (
          <Alert severity="warning">
            {readError} The document is kept and will be filed on the case.
          </Alert>
        ) : (
          <Alert severity="info">
            Check every field against the document. Nothing is saved until you press Create.
          </Alert>
        )}

        {/* ---------------- Client ---------------- */}
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <Typography variant="h6">Client</Typography>
            {matching && <CircularProgress size={16} />}
          </Stack>

          {lockedClient && (
            <Alert severity="info" sx={{ mb: 1 }}>
              The case will be added to {lockedClient.name}&apos;s record. Their details on file are not
              changed by anything below.
            </Alert>
          )}
          {!lockedClient && (
          <RadioGroup
            value={clientChoice ?? ''}
            onChange={(event) => {
              setChoiceTouched(true);
              setClientChoice(event.target.value);
            }}
          >
            {candidates.map((candidate) => (
              <Box key={candidate.userGuid} sx={{ mb: 1 }}>
                <FormControlLabel
                  value={candidate.userGuid}
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography component="span" fontWeight={600}>
                        {candidateName(candidate)}
                      </Typography>{' '}
                      <Typography component="span" variant="body2" color="text.secondary">
                        {[
                          candidate.dateOfBirth && `DOB ${candidate.dateOfBirth.slice(0, 10)}`,
                          candidate.phone,
                          candidate.email,
                          `${candidate.caseCount} case${candidate.caseCount === 1 ? '' : 's'}`,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </Typography>{' '}
                      <Link
                        component={RouterLink}
                        to={`/user?User_guid=${candidate.userGuid}`}
                        target="_blank"
                        rel="noopener"
                        variant="body2"
                      >
                        Open
                      </Link>
                      <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap' }}>
                        {candidate.reasons.map((reason) => (
                          <Chip
                            key={reason}
                            size="small"
                            label={REASON_LABELS[reason]}
                            color={reason === 'name-only' ? 'default' : 'primary'}
                            variant={reason === 'name-only' ? 'outlined' : 'filled'}
                          />
                        ))}
                      </Stack>
                      {candidate.warnings.map((warning) => (
                        <Typography key={warning} variant="body2" color="warning.main">
                          {warning}
                        </Typography>
                      ))}
                    </Box>
                  }
                />
              </Box>
            ))}
            <FormControlLabel value="new" control={<Radio />} label={<Typography fontWeight={600}>New client</Typography>} />
          </RadioGroup>
          )}

          {!lockedClient && !reading && candidates.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              No client on file matches this document.
            </Typography>
          )}
          {!lockedClient && !reading && clientChoice === null && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              More than one client could be this person, or the only match is by name. Choose one, or
              New client.
            </Alert>
          )}
          {chosenCandidate && (
            <Alert severity="info" sx={{ mt: 1 }}>
              The case will be added to {candidateName(chosenCandidate)}&apos;s record. Their details on
              file are not changed by anything below.
              {differences(form, chosenCandidate).map((line) => (
                <Typography key={line} variant="body2">
                  {line}
                </Typography>
              ))}
            </Alert>
          )}
          {isNewClient && strongCandidates.length > 0 && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              A client on file shares a licence, email, phone or date of birth with this document. A
              new client may be a second record for the same person.
            </Alert>
          )}
          {isNewClient && emailHeldBy && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              {form.email} is already on file for {emailHeldBy.name || 'another client'}. If this is a
              different person, the new client will be saved without an email and an alert raised to
              get their own.
            </Alert>
          )}

          {isNewClient && (
            <FormControl size="small" sx={{ mt: 2, minWidth: 260 }}>
              <InputLabel id="intake-client-source">Source</InputLabel>
              <Select
                labelId="intake-client-source"
                label="Source"
                value={clientSource}
                onChange={(event) => {
                  setClientSource(event.target.value);
                  if (event.target.value === REFERRAL_SOURCE_LABELS.OTR) setMarkOtr(true);
                }}
              >
                <MenuItem value="">
                  <em>Not specified</em>
                </MenuItem>
                {userSourceOptions(clientSource).map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                Who pays decides how the payment step opens: Google and repeat clients pay the firm.
              </Typography>
            </FormControl>
          )}

          <Divider sx={{ my: 2 }} />
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {isNewClient && !lockedClient
              ? 'The new client is created with these details.'
              : 'What the document says about the client. Used to find them; not saved to an existing client.'}
          </Typography>
          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
            {textField('firstName', 'First name', {
              required: isNewClient,
              error: isNewClient ? nameProblem(form.firstName, 'First name', true) : null,
            })}
            {textField('lastName', 'Last name', {
              required: isNewClient,
              error: isNewClient ? nameProblem(form.lastName, 'Last name', true) : null,
            })}
            {textField('middleName', 'Middle name', {
              error: isNewClient ? nameProblem(form.middleName, 'Middle name', false) : null,
            })}
            {textField('dateOfBirth', 'Date of birth', { type: 'date' })}
            {textField('driversLicenseNumber', "Driver's licence number")}
            <FormControl size="small" fullWidth>
              <InputLabel id="intake-licence-class">Licence class</InputLabel>
              <Select
                labelId="intake-licence-class"
                label="Licence class"
                value={form.licenseClass}
                onChange={set('licenseClass')}
              >
                <MenuItem value="">Not specified</MenuItem>
                <MenuItem value="Regular">Regular</MenuItem>
                <MenuItem value="CDL">CDL</MenuItem>
              </Select>
              <Typography variant="caption" color="text.secondary" sx={{ ml: 1.75, mt: 0.5 }}>
                {origin('licenseClass')}
              </Typography>
            </FormControl>
            {textField('phone', 'Phone')}
            {textField('email', 'Email')}
          </Box>
          <Box sx={{ mt: 2 }}>
            {textField('clientAddress', 'Home address', { multiline: true })}
            <Typography variant="caption" color="text.secondary">
              There is no address field on the client record, so this is saved as a note on the case.
            </Typography>
          </Box>
        </Paper>

        {/* ---------------- Case ---------------- */}
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Case
          </Typography>

          {duplicates.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {duplicates.map((duplicate) => (
                <Typography key={duplicate.Ticket_guid} variant="body2">
                  Citation {duplicate.Ticket_Number} is already on{' '}
                  <Link component={RouterLink} to={`/ticket?Ticket_guid=${duplicate.Ticket_guid}`} target="_blank" rel="noopener">
                    case {duplicate.Ticket_MatterNumber || duplicate.Ticket_Number}
                  </Link>{' '}
                  ({duplicate.clientName || 'no name'}
                  {duplicate.Ticket_Active ? '' : ', inactive'}).
                </Typography>
              ))}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={duplicateAcknowledged}
                    onChange={(event) => setDuplicateAcknowledged(event.target.checked)}
                  />
                }
                label="Create another case for this citation anyway"
              />
            </Alert>
          )}

          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
            {textField('ticketNumber', 'Citation number')}
            {textField('citation', 'Offense')}
            {textField('respondBy', 'Respond-by date', {
              type: 'date',
              required: true,
              error: form.respondBy ? null : 'The court respond-by date is required.',
            })}
            {textField('courtDate', 'Court date', { type: 'date' })}
            {textField('address', 'Violation address')}
            {textField('city', 'Violation city')}
            {textField('county', 'Violation county')}
            <FormControl size="small" fullWidth>
              <InputLabel id="intake-licence-type">Licence type</InputLabel>
              <Select
                labelId="intake-licence-type"
                label="Licence type"
                value={form.licenseType}
                onChange={set('licenseType')}
              >
                <MenuItem value="">Not specified</MenuItem>
                <MenuItem value="Class C">Class C</MenuItem>
                <MenuItem value="CDL">CDL</MenuItem>
                <MenuItem value="None">No License</MenuItem>
              </Select>
              <Typography variant="caption" color="text.secondary" sx={{ ml: 1.75, mt: 0.5 }}>
                {origin('licenseType')}
              </Typography>
            </FormControl>
            {textField('agency', 'Issuing agency')}
            {textField('agencyCaseNumber', 'Agency case number')}
            {textField('plate', 'Licence plate')}
            <FormControl size="small" fullWidth>
              <InputLabel id="intake-zone">Zone</InputLabel>
              <Select labelId="intake-zone" label="Zone" value={form.zone} onChange={set('zone')}>
                <MenuItem value="">None</MenuItem>
                <MenuItem value="Street">Street</MenuItem>
                <MenuItem value="Highway">Highway</MenuItem>
                <MenuItem value="Interstate">Interstate</MenuItem>
                <MenuItem value="Parking">Parking</MenuItem>
                <MenuItem value="School Zone">School Zone</MenuItem>
                <MenuItem value="Construction Zone">Construction Zone</MenuItem>
                <MenuItem value="Other">Other</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ mt: 2 }}>
            <GenderPronounFields
              gender={gender}
              pronounSet={pronounSet}
              onGenderChange={(nextGender, nextPronounSet) => {
                setGender(nextGender);
                setPronounSet(nextPronounSet);
              }}
            />
            <FormControlLabel
              control={<Checkbox checked={hasTakenDsc} onChange={(event) => setHasTakenDsc(event.target.checked)} />}
              label="Has taken a driver safety course in the past year"
            />
          </Box>
        </Paper>

        {/* ---------------- Court, prosecutor, plan ---------------- */}
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Court and plan
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {printedCourt ? (
              <>
                Printed on the document: <strong>{printedCourt}</strong>.{' '}
                {courtMatch?.status === 'matched'
                  ? 'Matched to a court on our list; check it below.'
                  : courtMatch?.status === 'unsure'
                    ? 'Not sure which of our courts this is.'
                    : 'Not matched to any court on our list. Choose one below.'}
              </>
            ) : (
              'No court was read from the document.'
            )}
          </Typography>
          {courtMatch?.status === 'unsure' && courtMatch.candidates.length > 0 && (
            <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
              {courtMatch.candidates.map((candidate) => (
                <Chip
                  key={candidate.Court_id}
                  label={`${candidate.Court_Name}${candidate.Court_County ? ` (${candidate.Court_County})` : ''}`}
                  onClick={() => applyCourtGuid(candidate.Court_guid, false)}
                  color={court?.Court_guid === candidate.Court_guid ? 'primary' : 'default'}
                />
              ))}
            </Stack>
          )}
          <Stack spacing={2}>
            <CourtSelect
              value={court}
              onChange={(next) => {
                setCourt(next);
                if (prosecutor && prosecutor.court?.Court_guid !== next?.Court_guid) setProsecutor(null);
              }}
              label="Court"
              placeholder="Search and select a court..."
              required
              error={!court}
              helperText={court ? undefined : 'Court is required'}
            />
            <ProsecutorSelect
              value={prosecutor}
              onChange={setProsecutor}
              label="Prosecutor (optional)"
              placeholder="Search and select a prosecutor..."
              disabled={!court}
              courtGuidFilter={court?.Court_guid}
              helperText={!court ? 'Please select a court first' : undefined}
            />
            <FormControl fullWidth size="small" error={!plan}>
              <InputLabel id="intake-plan">Plan (required)</InputLabel>
              <Select labelId="intake-plan" label="Plan (required)" value={plan} onChange={(event) => setPlan(event.target.value)}>
                {matchingProducts.length === 0 ? (
                  <MenuItem disabled value="">
                    No matching plans available
                  </MenuItem>
                ) : (
                  matchingProducts.map((product) => (
                    <MenuItem key={product.Product_guid} value={product.Product_Name}>
                      {product.Product_Name} — ${(product.Product_Cost / 100).toFixed(2)}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          </Stack>
        </Paper>

        {/* ---------------- Referral and document ---------------- */}
        <Paper variant="outlined" sx={{ p: 2 }}>
          {parsed?.referralPartner === 'OTR' && (
            <Alert severity="info" sx={{ mb: 1 }}>
              This looks like an Off the Record referral
              {parsed.referralEvidence ? ` (“${parsed.referralEvidence}”)` : ''}. Tick the box if
              it is.
            </Alert>
          )}
          <FormControlLabel
            control={<Checkbox checked={markOtr} onChange={(event) => setMarkOtr(event.target.checked)} />}
            label="This is an Off the Record (OTR) referral"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={isCitation ?? false}
                onChange={(event) => setIsCitation(event.target.checked)}
              />
            }
            label="This document is the citation (shown on the case's citation card)"
          />
          {!existingDocumentGuid && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={clientVisible ?? false}
                  onChange={(event) => setClientVisible(event.target.checked)}
                />
              }
              label="The client can see this document in their portal"
            />
          )}
        </Paper>

        {/* ---------------- Payment link ---------------- */}
        <SendPaymentLinkBox {...paymentBox.view} />

        {submitError && <Alert severity="error">{submitError}</Alert>}
        {paymentBox.problem && <Alert severity="warning">{paymentBox.problem}</Alert>}
        {missing.length > 0 && (
          <Alert severity="warning">
            Before you can create:
            <Box component="ul" sx={{ m: 0, pl: 2 }}>
              {missing.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </Box>
          </Alert>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', pb: 2 }}>
          <Button
            variant="contained"
            size="large"
            disabled={missing.length > 0 || Boolean(paymentBox.problem) || submitting}
            onClick={() => void create()}
            startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : undefined}
          >
            {submitting
              ? 'Creating…'
              : `${isNewClient ? 'Create client and case' : 'Create case'}${paymentBox.willSend ? ' and send payment link' : ''}`}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
};

export default CreateCaseReview;
